import { env } from "@/config/env"
import { HttpClient } from "@/shared/lib/http-client"

import type { AuraScanPublicUser } from "../types"

const RECEIVED_AURA_SCAN_REPORT_KEY = "RECEIVED_AURA_SCAN_REPORT"

function alreadyRequested(key: string): boolean {
  try {
    return window.sessionStorage.getItem(RECEIVED_AURA_SCAN_REPORT_KEY) === key
  } catch {
    return false
  }
}

function markRequested(key: string): void {
  try {
    window.sessionStorage.setItem(RECEIVED_AURA_SCAN_REPORT_KEY, key)
  } catch {
    /* ignore */
  }
}

class AuraScanReportService extends HttpClient {
  /** Public (quiz / additional-offers): pre-account aura report from a quiz snapshot. */
  async sendPublic(params: {
    user: AuraScanPublicUser
    email: string
    subscriptionId: string
  }): Promise<void> {
    const email = params.email.toLowerCase()
    if (alreadyRequested(`aura-scan:${email}`)) return

    await this.post("/v1/reports/aura-scan", {
      subscriptionId: params.subscriptionId,
      email,
      user: params.user,
    })

    markRequested(`aura-scan:${email}`)
  }

  /** Authed (webapp): backend derives the profile from the Firebase token. */
  async sendForMe(params: { subscriptionId: string; token: string }): Promise<void> {
    const key = `me:aura-scan:${params.subscriptionId}`
    if (alreadyRequested(key)) return

    await this.post(
      "/v1/reports/me/aura-scan",
      { subscriptionId: params.subscriptionId },
      { headers: { Authorization: `Bearer ${params.token}` } },
    )

    markRequested(key)
  }
}

const auraScanReportService = new AuraScanReportService({ baseURL: env.VITE_API_URL })

/**
 * Quiz / additional-offers (pre-account): request the Aura Scan report via the
 * public `POST /v1/reports/aura-scan` with a raw quiz snapshot. Best-effort.
 */
export async function requestAuraScanReport(params: {
  user: AuraScanPublicUser
  email: string
  subscriptionId: string
}): Promise<void> {
  if (!env.VITE_API_URL) return
  await auraScanReportService.sendPublic(params)
}

/**
 * Webapp: request the Aura Scan report for the signed-in user via the authed
 * `POST /v1/reports/me/aura-scan` (subscription id only; backend reads the
 * profile and verifies the subscription from the token). Best-effort.
 * Idempotent on `subscriptionId`.
 */
export async function requestMyAuraScanReport(params: {
  subscriptionId: string
  token: string
}): Promise<void> {
  if (!env.VITE_API_URL) return
  await auraScanReportService.sendForMe(params)
}
